import React, { memo } from "react";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { cn } from "@/lib/utils";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export interface LabelProps {
  /** Label text (translation key). */
  label: TranslationKeys;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
  /** Extra Tailwind classes merged onto the root AppText. */
  className?: string;
  /** Forwarded to the root AppText for testing. */
  testID?: string;
}

/**
 * Form field label with optional required asterisk.
 *
 * Renders translated label text with medium weight, small size, and dark text color.
 * Includes a red asterisk when `required` is true.
 *
 * @example
 * ```tsx
 * <Label label="form.email" required />
 * ```
 */
export const Label = memo(function Label({ label, required, className, testID }: LabelProps) {
  const { t, isRTL } = useI18n();

  return (
    <AppText
      weight="medium"
      className={cn("text-sm text-dark-text-20 mb-1", isRTL ? "text-end" : "text-start", className)}
      testID={testID}
    >
      {t(label)}
      {required && <AppText className="text-error"> *</AppText>}
    </AppText>
  );
});
